import {Injectable} from '@angular/core';
import {BehaviorSubject, fromEvent, Observable} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class VisibilityService {
  private isVisible$ = new BehaviorSubject<boolean>(this.isDocumentVisible());
  private isMouseActive$ = new BehaviorSubject<boolean>(true);
  private isActive$ = new BehaviorSubject<boolean>(this.isDocumentVisible() && this.isMouseActive$.value);
  private mouseActivityTimeout: number | null = null;
  private readonly MOUSE_INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

  constructor() {
    // Listen for visibility change events
    fromEvent(document, 'visibilitychange').subscribe(() => {
      this.isVisible$.next(this.isDocumentVisible());
      this.updateCombinedState();
    });

    // Setup mouse activity tracking
    this.setupMouseActivityTracking();
  }

  /**
   * Get an observable that emits when the page visibility changes
   * @returns Observable<boolean> - true if the page is visible, false otherwise
   */
  public getVisibilityState(): Observable<boolean> {
    return this.isVisible$.asObservable();
  }

  /**
   * Get an observable that emits when either visibility or mouse activity changes
   * @returns Observable<boolean> - true if the page is visible and mouse is active
   */
  public getActiveState(): Observable<boolean> {
    return this.isActive$.asObservable();
  }

  /**
   * Get an observable that emits when mouse activity state changes
   * @returns Observable<boolean> - true if the mouse has been active in the last 5 minutes
   */
  public getMouseActivityState(): Observable<boolean> {
    return this.isMouseActive$.asObservable();
  }

  /**
   * Get the current visibility state
   * @returns boolean - true if the page is visible, false otherwise
   */
  public isPageVisible(): boolean {
    return this.isDocumentVisible();
  }

  /**
   * Get the current mouse activity state
   * @returns boolean - true if the mouse has been active in the last 5 minutes
   */
  public isMouseActive(): boolean {
    return this.isMouseActive$.value;
  }

  /**
   * Get the current combined state (visible and mouse active)
   * @returns boolean - true if the page is visible and mouse is active
   */
  public isActive(): boolean {
    return this.isDocumentVisible() && this.isMouseActive$.value;
  }

  private isDocumentVisible(): boolean {
    return document.visibilityState === 'visible';
  }

  private updateCombinedState(): void {
    this.isActive$.next(this.isDocumentVisible() && this.isMouseActive$.value);
  }

  private setupMouseActivityTracking(): void {
    // Mouse events to track
    const mouseEvents = ['mousemove', 'mousedown', 'mouseup', 'click'];

    // Reset the inactivity timer whenever any mouse event occurs
    mouseEvents.forEach(eventName => {
      fromEvent(document, eventName).subscribe(() => {
        this.resetMouseInactivityTimer();
      });
    });

    // Set initial mouse activity state to true
    this.resetMouseInactivityTimer();
  }

  private resetMouseInactivityTimer(): void {
    // Clear any existing timeout
    if (this.mouseActivityTimeout) {
      clearTimeout(this.mouseActivityTimeout);
    }

    // If mouse was previously inactive, mark as active
    if (!this.isMouseActive$.value) {
      this.isMouseActive$.next(true);
      this.updateCombinedState();
    }

    // Set a new timeout for inactivity
    this.mouseActivityTimeout = setTimeout(() => {
      this.isMouseActive$.next(false);
      this.updateCombinedState();
    }, this.MOUSE_INACTIVITY_TIMEOUT);
  }
}
